import type { FC } from 'react';
import clsx from 'clsx';
import type { StrategyFilter } from './hooks/useStrategiesData';
import { useStrategiesData } from './hooks/useStrategiesData';
import { StrategyCard } from './StrategyCard';
import { StrategyModal } from './StrategyModal';
import styles from './Strategies.module.css';

const FILTER_OPTIONS: { value: StrategyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'passive', label: 'Passive' },
];

export const Strategies: FC = () => {
  const {
    filteredStrategies,
    loading,
    modalMode,
    selectedStrategy,
    filter,
    setFilter,
    formData,
    setFormData,
    handleEdit,
    handleCreate,
    handleCloseModal,
    handleSave,
    handleDelete,
  } = useStrategiesData();

  if (loading) {
    return (
      <div className={styles.container} aria-busy="true">
        <p>Loading strategies...</p>
      </div>
    );
  }

  return (
    <div className={styles.container} aria-busy="false">
      <div className={styles.header}>
        <h1 className={styles.title}>Security Testing Strategies</h1>
        <button
          className={styles.button}
          data-variant="primary"
          onClick={handleCreate}
          aria-label="Create new strategy"
        >
          + Create New Strategy
        </button>
      </div>

      <div className={styles.filterBar} role="tablist" aria-label="Filter strategies by type">
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            role="tab"
            aria-selected={filter === option.value}
            className={clsx(styles.filterButton, { [styles.filterButtonActive]: filter === option.value })}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
        <span className={styles.filterCount}>{filteredStrategies.length} strategies</span>
      </div>

      {filteredStrategies.length === 0 ? (
        <div className={styles.emptyState}>
          <h3>No Strategies Found</h3>
          <p>{filter === 'all' ? 'Create your first security testing strategy' : `No ${filter} strategies found`}</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {filteredStrategies.map((strategy) => (
            <StrategyCard key={strategy.id} strategy={strategy} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {modalMode !== null && (
        <StrategyModal
          isEdit={modalMode === 'edit'}
          formData={formData}
          onFormChange={setFormData}
          onSave={handleSave}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};
